// :cow: Cosmose CONFIDENTIAL :iso:
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concatMap, map } from 'rxjs/operators';
import { AuthData, LoginCredentials } from '../model';
import { Observable, of, throwError } from 'rxjs';
import { ConfigService } from '@med/app/core/config/config.service';
import { decodeToken } from '@med/app/core/utils/decode-token';


@Injectable()
export class AuthRepository {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
  ) {
  }

  authorize(credentials: LoginCredentials): Observable<AuthData> {
    let params = new HttpParams()
      .set('username', credentials.username)
      .set('password', credentials.password)
      .set('grant_type', 'password');

    if (credentials.twoFACode) {
      params = params.set('2fa_code', credentials.twoFACode);
    }

    return this.http
      .post<AuthData>(
        `${ this.configService.getConfig().apiUrl }/uaa/oauth/token`,
        params.toString(),
        {headers: this.getAuthHeaders()},
      ).pipe(
        concatMap(value => {
          // dirty hack for users with no authorities
          const decoded = decodeToken(value.access_token);
          if (!decoded.authorities || decoded.authorities.length === 0) {
            const error = new HttpErrorResponse({error: 'No authorities assigned'});

            return throwError(error);
          }

          return of(value);
        }),
      );
  }

  reauthorize(refreshToken: string): Observable<AuthData> {
    const params = new HttpParams()
      .set('refresh_token', refreshToken)
      .set('grant_type', 'refresh_token')
      .toString();

    return this.http
      .post<AuthData>(
        `${ this.configService.getConfig().apiUrl }/uaa/oauth/token`,
        params,
        {headers: this.getAuthHeaders()},
      );
  }

  resetPassword(username: string): Observable<void> {
    return this.http
      .post<void>(
        `${ this.configService.getConfig().apiUrl }/uaa/users/password/reset`,
        {username},
        {headers: {Authorization: this.getAuthHeaders().Authorization}},
      );
  }

  changePassword(username: string, currentPassword: string, newPassword: string): Observable<void> {
    return this.http
      .post(
        `${ this.configService.getConfig().apiUrl }/uaa/users/password/change`,
        {
          username,
          currentPassword,
          newPassword,
        },
        {headers: {Authorization: this.getAuthHeaders().Authorization}},
      ).pipe(map(() => null));
  }

  public disableTwoFa(code: string): Observable<boolean> {
    const payload = {code};
    const url = `${ this.configService.getConfig().apiUrl }/uaa/two-factor-authentication/disable-2fa`;

    return this.http.put<boolean>(url, payload);
  }

  private getAuthHeaders() {
    return {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Basic ${ this.configService.getConfig().securityToken }`,
    };
  }
}
